<template>
    <div>
        <notification-container :notifications="notifications"/>
        <div class="modal fade" id="transferEntryModal" tabindex="-1" role="dialog" aria-labelledby="transferModalLabel"
         aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h4 class="modal-title" id="transferModalLabel">{{ $t('transfer_entry.modal_title') }}</h4>
                        <button type="button" class="close" data-dismiss="modal" :aria-label="$t('transfer_entry.close')"><span
                            aria-hidden="true">&times;</span>
                        </button>
                    </div>

                    <!-- Transfer in progress state -->
                    <template v-if="participant.transfer_guid">
                        <div class="modal-body">
                            <p>{{ $t('transfer_entry.transfer_in_progress') }}</p>
                            <p>{{ $t('transfer_entry.pending_response', { name: participant.transfer_to_name, email: participant.transfer_to_email }) }}</p>
                        </div>
                        <div class="modal-footer">
                            <button
                                @click="cancelTransfer"
                                class="btn btn-danger"
                            >
                                {{ isLoading ? $t('transfer_entry.cancelling') : $t('transfer_entry.cancel_transfer') }}
                            </button>
                        </div>
                    </template>

                    <!-- Waiting list transfer in progress -->
                    <template v-else-if="isTransferTypeWaitingList">
                        <div class="modal-body">
                            <p>{{ $t('transfer_entry.waiting_list_progress') }}</p>
                        </div>
                    </template>

                    <!-- Already transferred -->
                    <template v-else-if="participant.transferred_to_participant">
                        <div class="modal-body">
                            <p>{{ $t('transfer_entry.already_transferred', { name: participant.transferred_to_participant.fullname }) }}</p>
                        </div>
                    </template>

                    <!-- Transfer form -->
                    <template v-else>
                        <form @submit.prevent="submitTransfer">
                            <div class="modal-body">
                                <p>{{ $t('transfer_entry.provide_details') }}</p>

                                <div v-if="allowTransferToWaitingList || isAdmin" class="form-group">
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            name="transfer_type"
                                            id="transfer_friend"
                                            value="friend"
                                            v-model="transferType"
                                        >
                                        <label class="form-check-label" for="transfer_friend">
                                            {{ $t('transfer_entry.transfer_to_friend') }}
                                        </label>
                                    </div>
                                    <div class="form-check">
                                        <input
                                            class="form-check-input"
                                            type="radio"
                                            name="transfer_type"
                                            id="transfer_waiting_list"
                                            value="waiting_list"
                                            v-model="transferType"
                                        >
                                        <label class="form-check-label" for="transfer_waiting_list">
                                            {{ $t('transfer_entry.transfer_to_waiting_list') }}
                                        </label>
                                    </div>
                                </div>

                                <div v-if="transferType === 'friend'" id="friend-form">
                                    <div class="form-group">
                                        <label for="transfer_to_name">{{ $t('transfer_entry.name') }}</label>
                                        <input
                                            type="text"
                                            class="form-control"
                                            id="transfer_to_name"
                                            v-model="transferToName"
                                            required
                                        >
                                    </div>
                                    <div class="form-group">
                                        <label for="transfer_to_email">{{ $t('transfer_entry.email') }}</label>
                                        <input
                                            type="email"
                                            class="form-control"
                                            id="transfer_to_email"
                                            v-model="transferToEmail"
                                            required
                                        >
                                    </div>
                                </div>

                                <div v-if="transferType === 'waiting_list'" id="waiting-list-form">
                                    <p>{{ $t('transfer_entry.waiting_list_agreement') }}</p>
                                    <p>{{ $t('transfer_entry.waiting_list_agreement_refund_note', {refundAmount: refundAmount}) }}</p>
                                    <p><strong>NOTE:</strong> {{ $t('transfer_entry.waiting_list_note') }}</p>
                                </div>
                            </div>
                            <div class="modal-footer">
                                <button
                                    type="submit"
                                    class="btn-success btn btn-block btn-flat"
                                    :disabled="isLoading || !isFormValid"
                                >
                                    {{ isLoading ? $t('transfer_entry.transferring') : $t('transfer_entry.transfer_button') }}
                                </button>
                            </div>
                        </form>
                    </template>

                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NotificationContainer from '../../Common/NotificationContainer.vue'
import notificationsMixin from '../../../mixins/notifications.js'
import {money} from "../../Utility/filters/money.js";

export default {
    name: 'TransferEntryModalForm',
    mixins: [notificationsMixin],
    components: {
        NotificationContainer
    },
    props: {
        participant: {
            type: Object,
            required: true,
            default: () => ({})
        },
        isAdmin: {
            type: Boolean,
            default: false
        },
        submitUrl: {
            type: String,
            required: true
        },
        cancelUrl: {
            type: String,
            required: true
        },
    },
    filters: {
        money,
    },
    data() {
        return {
            transferType: 'friend',
            transferToName: '',
            transferToEmail: '',
            isLoading: false,
        }
    },
    computed: {
        allowTransferToWaitingList() {
            return this.participant.registration_category && this.participant.registration_category.allow_transfer_to_waiting_list;
        },
        isTransferTypeWaitingList() {
            return this.participant.transfer_type === 'waiting_list';
        },
        isFormValid() {
            // if (this.transferType === 'friend') {
            //     return this.transferToName.trim() !== '' && this.transferToEmail.trim() !== '';
            // }
            return true;
        },
        refundAmount() {
            if (this.participant.invoice_item === undefined) {
                return 0;
            }

            let feeAmount = this.participant.invoice_item.fee_amount || 0;
            let customFee = this.participant.invoice_item.custom_fee_amount || 0;

            const bookingFeeAmount = feeAmount + customFee;

            return this.$options.filters.money(
                (this.participant.invoice_item.total_amount - bookingFeeAmount),
                this.participant.invoice_item.currency,
            );
        }
    },
    methods: {
        async submitTransfer() {
            if (!this.isFormValid) {
                return;
            }

            this.isLoading = true;

            const transferData = {
                transfer_type: this.transferType
            };

            if (this.transferType === 'friend') {
                transferData.transfer_to_name = this.transferToName;
                transferData.transfer_to_email = this.transferToEmail;
            }

            try {
                const response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.post(this.submitUrl, transferData, {
                        headers: {
                            'Authorization': jwtToken,
                            'Content-Type': 'application/json'
                        }
                    });
                });

                if (response.data.data.status !== 'success') {
                    this.showAlertMessage(response.data.data.message || this.$t('transfer_entry.transfer_failed'), 'error', 5000);
                    this.$emit('transfer-error', this.$t('transfer_entry.transfer_failed'));
                    return;
                }

                this.$emit('transfer-success', response.data);
                this.resetForm();
                $('#transferEntryModal').modal('hide');
                this.showAlertMessage(this.$t('transfer_entry.transfer_success'), 'success', 5000);

                setTimeout(() => {
                    window.location.reload();
                }, 200);
            } catch (error) {
                console.log(error, "error");
                this.showAlertMessage(this.$t('transfer_entry.transfer_error'), 'error', 3000);

                if (error.response && error.response.data && error.response.data.message) {
                    this.$emit('transfer-error', error.response.data.message);
                } else {
                    this.$emit('transfer-error', this.$t('transfer_entry.general_error'));
                }
            } finally {
                this.isLoading = false;
            }
        },

        async cancelTransfer() {
            if (!confirm(this.$t('transfer_entry.cancel_confirm'))) {
                return;
            }

            this.isLoading = true;

            try {
                const response = await this.$root.makeApiRequest(async (jwtToken) => {
                    return await axios.post(this.cancelUrl, {}, {
                        headers: {
                            'Authorization': jwtToken,
                            'Content-Type': 'application/json'
                        }
                    });
                });

                // Handle JSend format responses
                if (response.data.status !== 'success' && !response.data.success) {
                    this.showAlertMessage(response.data.message || this.$t('transfer_entry.cancel_failed'), 'error', 3000);
                    this.$emit('transfer-error', this.$t('transfer_entry.cancel_failed'));
                    return;
                }

                this.$emit('cancel-transfer-success', response.data);
                $('#transferEntryModal').modal('hide');
                this.showAlertMessage(this.$t('transfer_entry.cancel_success'), 'success', 3000);

                setTimeout(() => {
                    window.location.reload();
                }, 200);
            } catch (error) {
                this.showAlertMessage(this.$t('transfer_entry.cancel_error'), 'error', 3000);

                if (error.response && error.response.data && error.response.data.message) {
                    this.$emit('transfer-error', error.response.data.message);
                } else {
                    this.$emit('transfer-error', this.$t('transfer_entry.cancel_general_error'));
                }
            } finally {
                this.isLoading = false;
            }
        },

        resetForm() {
            this.transferType = 'friend';
            this.transferToName = '';
            this.transferToEmail = '';
        }
    },
    mounted() {
        // Reset form when modal is shown
        $('#transferEntryModal').on('show.bs.modal', () => {
            this.resetForm();
        });
    }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/Assets/components/Participants/Form/TransferEntryModalForm.vue